/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import {
  TimeComponentLayersStack,
  TimeComponentVerticalGridlines as GanttVerticalGridlinesLayer
} from '../PRIVATE_TimeComponent';
import { GanttHorizontalGridlinesLayer } from './GanttHorizontalGridlinesLayer';
import { GanttTaskAccLayer } from './GanttTaskAccLayer';
import { GanttRowBackgroundsLayer } from './GanttRowBackgroundsLayer';
import { GanttTaskEffectsLayer } from './GanttTaskEffectsLayer';
import { GanttTaskLabelsLayer } from './GanttTaskLabelsLayer';
import { GanttTasksLayer } from './GanttTasksLayer';
import type { getEffectTaskObjs, ViewportLayout } from './utils/dataLayoutUtils';
import type { GanttRow, GanttTask } from './Gantt.types';

type Props<K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>> = {
  width: number;
  height: number;
  minorAxisLayout: {
    minorGridTicksPos: number[];
    majorGridTicksPos?: number[];
  };
  viewportLayout: ViewportLayout<K1, K2, D1, D2>;
  effectsLayout: ReturnType<typeof getEffectTaskObjs<K2>>;
  dateFormatter: (date: string) => string;
  ariaActiveTask?: {
    dataId: K2;
    ariaId: string;
  };
};

export function GanttDataBody<K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>>({
  width,
  height,
  minorAxisLayout: { minorGridTicksPos, majorGridTicksPos },
  viewportLayout: {
    gridlines,
    rowData,
    rowObjs,
    taskLabelObjs,
    horizontalGridlinesPos,
    idTaskObjMap
  },
  effectsLayout,
  dateFormatter,
  ariaActiveTask
}: Props<K1, K2, D1, D2>) {
  return (
    <TimeComponentLayersStack width={`${width}px`} height={`${height}px`}>
      <GanttRowBackgroundsLayer width={width} height={height} layoutObjs={rowObjs} />
      {gridlines.vertical === 'on' && (
        <GanttVerticalGridlinesLayer
          width={width}
          height={height}
          positions={minorGridTicksPos}
          variant="minor"
        />
      )}
      {gridlines.vertical === 'on' && majorGridTicksPos && (
        <GanttVerticalGridlinesLayer
          width={width}
          height={height}
          positions={majorGridTicksPos}
          variant="major"
        />
      )}
      {horizontalGridlinesPos && (
        <GanttHorizontalGridlinesLayer
          width={width}
          height={height}
          positions={horizontalGridlinesPos}
        />
      )}
      <GanttTaskAccLayer
        width={width}
        height={height}
        layoutObjs={rowObjs}
        rowData={rowData}
        dateFormatter={dateFormatter}
        ariaActiveId={ariaActiveTask?.ariaId}
        ariaActiveTaskObj={ariaActiveTask && idTaskObjMap.get(ariaActiveTask.dataId)}
      />
      <GanttTasksLayer width={width} height={height} layoutObjs={rowObjs} />
      <GanttTaskEffectsLayer width={width} height={height} layoutObjs={effectsLayout} />
      <GanttTaskLabelsLayer width={width} height={height} layoutObjs={taskLabelObjs} />
    </TimeComponentLayersStack>
  );
}
